import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import { BUTTON_VARIANTS, INPUT_TYPES } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCancel } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { useInspectionCancelModalViewModel } from "./useInspectionCancelModalViewModel";

interface InspectionCancelModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: (data: { reason: string }) => Promise<void>;
}

export const InspectionCancelModal = ({
  open,
  onClose,
  onCancel,
}: InspectionCancelModalProps) => {
  const { classes, theme } = useStyles();

  const { loadCancelReasons, cancelReasonOptions, isLoading } =
    useInspectionCancelModalViewModel();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      reason: "",
    },
    resolver: zodResolver(
      z.object({
        reason: z.string().min(1, "Cancel Reason is required."),
      })
    ),
  });

  const cancelModalFields = [
    {
      label: "Cancel Reason",
      name: "reason",
      required: true,
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: cancelReasonOptions,
    },
  ];

  const handleFormSubmit = async (data: { reason: string }) => {
    await onCancel(data);
  };

  useEffect(() => {
    loadCancelReasons();
  }, []);

  return (
    <MiModal
      open={open}
      closeModal={onClose}
      titleIcon={mdiCancel}
      leftIconColor={theme.palette.red.main}
      title="Cancel Inspection"
      loading={isLoading}
      actions={[
        {
          name: "Close",
          variant: BUTTON_VARIANTS.TEXT,
          color: theme.palette.grey[700],
          onClick: onClose,
        },
        {
          name: "Cancel Inspection",
          color: theme.palette.red.main,
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: !isValid || !isDirty || isLoading,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Box>
        <Typography className={classes.cancelText}>
          Please select a reason for cancelling this inspection order.
        </Typography>
        <MiDetailFields detailFields={cancelModalFields} />
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  cancelText: {
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(2),
  },
}));
